import { FarrisPropConfigWithData, ElementPropertyConfig, IFarrisPropertyPanelOperation, PmfEvents, ProcessCell } from "@edp-pmf/grapheditor";
import { BpmnModel, Process, BPMNPlane, BpmnModelHelper, ExtensionElement, DataTypes, DataType, DataObject } from "@edp-pmf/bpmn-model";
import { ProcessHeader, StartMode, Component, ParticipantExpression, DurationType, CDTSchemaType, ToolTime, ExecuteForm, VariableSetting, ActualParameter, TitleExpression, CustomizeAction, MessageNotice, TimeLimitControl, ExtendProperty, ImageSettings, PositionParticipantExpression, UserParticipantExpression, ValuedDataObject, TimeLimitControlInfo, SendMessageExpireOperationInfo, SendMessageExpireOperation, AutoHandleExpireOperation, AutoHandleExpireOperationInfo, ActivityExecutorParticipantExpression, SmsMessageTemplate, SmsMessageVariable, } from "@gsp-wf/wf-process-model";
import { mxEventObject, mxResources } from "@edp-pmf/mxgraph-ts";
import { WfEditorUi } from "../WfEditorUi";
import { MessageReceiver } from "../entity/MessageReceiver";
import { BizComponent } from "../entity/BizComponent";
import { BizParameter } from "../entity/BizParameter";
import { WfDesignerUtils } from "../WfDesignerUtils";
import { WfEvents } from "../WfEvents";
import { ProcessPropertyKeys } from "./property-keys/ProcessPropertyKeys";
import { PropertyHelper } from "./PropertyHelper";
import { ProcessDesignerUIState } from "../../app/process-designer.uistate";
import { WfLocalePipe } from "../../i18n/pipe/wf-process-editor-locale.pipe";

/**
 * 流程元素类。
 * 取名虽然Cell，但实际上不需要继承mxCell以及Cell类，仅持有流程与画布实例，方法属性获取与修改操作。
 */
export class WfProcessCell extends ProcessCell {
    process: Process;
    plane: BPMNPlane;
    editorUi: WfEditorUi;
    uiState: ProcessDesignerUIState;

    getFarrisPropConfig(): FarrisPropConfigWithData {
        let extendList = [];
        if (this.editorUi.procExtProps && this.editorUi.procExtProps.length > 0) {
            extendList = JSON.parse(JSON.stringify(this.editorUi.procExtProps)).sort((a, b) => {
                return a.sortOrder - b.sortOrder;
            });
        }
        extendList = extendList.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
        });

        let propertyData = {};
        let usual = new ElementPropertyConfig();
        usual.categoryId = 'usual';
        usual.categoryName =  mxResources.get("basicInfo");
        usual.tabId = 'basic';
        usual.tabName =  mxResources.get("basic");
        usual.properties = [
            { propertyID: ProcessPropertyKeys.Name, propertyName:  mxResources.get("name"), propertyType: 'string', visible: !this.editorUi.multiLangEnabled },
            { propertyID: ProcessPropertyKeys.NameLanguage, propertyName:  mxResources.get("nameLanguage"), propertyType: 'multiLanguage', visible: this.editorUi.multiLangEnabled },
            { propertyID: ProcessPropertyKeys.Key, propertyName:  mxResources.get("key"), propertyType: 'string', readonly: true },
            { propertyID: ProcessPropertyKeys.EntryForm, propertyName:  mxResources.get("entryForm"), propertyType: 'modal' },
            { propertyID: ProcessPropertyKeys.startMode, propertyName:  mxResources.get("startMode"), propertyType: 'select' },
            { propertyID: ProcessPropertyKeys.wf_TitleExpression, propertyName:  mxResources.get("wfTitle"), propertyType: 'modal' },
        ];

        let formDef = new ElementPropertyConfig();
        formDef.categoryId = 'formDef';
        formDef.categoryName =  mxResources.get("formDef");
        formDef.tabId = 'basic';
        formDef.tabName =  mxResources.get("basic");
        formDef.properties = [
            { propertyID: ProcessPropertyKeys.wf_ExecuteForms_PC, propertyName:  mxResources.get("formsPC"), propertyType: 'modal' },
            { propertyID: ProcessPropertyKeys.wf_ExecuteForms_Mobile, propertyName:  mxResources.get("formsMobile"), propertyType: 'modal' },
        ];

        let process = new ElementPropertyConfig();
        process.categoryId = 'process';
        process.categoryName =  mxResources.get("process");
        process.tabId = 'advanced';
        process.tabName =  mxResources.get("advanced");
        process.properties = [
            { propertyID: ProcessPropertyKeys.wf_Component, propertyName:  mxResources.get("wfComponent"), propertyType: 'modal' },
        ];

        let advanced = new ElementPropertyConfig();
        advanced.categoryId = 'advanced';
        advanced.categoryName =  mxResources.get("otherConfig");
        advanced.tabId = 'advanced';
        advanced.tabName =  mxResources.get("advanced");
        advanced.properties = [
            { propertyID: ProcessPropertyKeys.processVariables, propertyName:  mxResources.get("processVariables"), propertyType: 'modal' },
            { propertyID: ProcessPropertyKeys.wf_CustomizeAction, propertyName:  mxResources.get("customizeAction"), propertyType: 'modal' },
            { propertyID: ProcessPropertyKeys.enablePriority, propertyName:  mxResources.get("enablePriority"), propertyType: 'select' },
        ];

        let processLocal = this.process;
        if (processLocal) {
            let extendElements: ExtensionElement[] = processLocal.GetExtensionElements();
            for (let i in extendElements) {
                if (extendElements[i] instanceof ProcessHeader) {
                    if((extendElements[i] as ProcessHeader).startMode === StartMode.DIRECT) {
                        advanced.properties = advanced.properties.concat({ propertyID: ProcessPropertyKeys.enableWholeOrderRetrieve, propertyName:  mxResources.get("enableWholeOrderRetrieve"), propertyType: 'select', description: mxResources.get("description") });
                    }
                }
            }
        }

        advanced.properties = advanced.properties.concat(                        { propertyID: ProcessPropertyKeys.imageSettings, propertyName:  mxResources.get("messageProperty"), propertyType: 'modal' });

        const time = new ElementPropertyConfig();
        time.categoryId = 'messageProperty';                                                             // 消息属性
        time.categoryName =  mxResources.get("timelinessInfo");
        time.tabId = 'advanced';
        time.tabName =  mxResources.get("advanced");
        time.properties = [
            { propertyID: ProcessPropertyKeys.timeLimitControl, propertyName:  mxResources.get("timeLimitControl"), propertyType: 'modal' },
            { propertyID: ProcessPropertyKeys.messageNotice, propertyName:  mxResources.get("messageNotice"), propertyType: 'modal' },
        ];

        // 扩展属性分group处理，每个group包含父级属性和属于它的子扩展属性
        const groupList = new Array();
        for (const extend of extendList) {
            if (!extend.parentId) {
                const group = { parent: extend, childs: new Array() };
                for (const e of extendList) {
                    if (e.parentId === extend.id) {
                        group.childs.push(e);
                    }
                }
                groupList.push(group);
            }
        }
        // 没有扩展属性值的时候不显示扩展属性一栏
        if (groupList && groupList.length > 0) {
            const extendProperty = new ElementPropertyConfig();
            extendProperty.categoryId = 'extendProperty';
            extendProperty.categoryName =  mxResources.get("extendProperty");
            extendProperty.tabId = 'advanced';
            extendProperty.tabName =  mxResources.get("advanced");
            extendProperty.properties = new Array();
            // 组织扩展属性的二级list
            for (const g of groupList) {
                extendProperty.properties.push(
                    { propertyID: g.parent.id, propertyName: g.parent.propertyTitle, propertyType: 'modal' },
                );
            }
            this.setProcessPropertyValue(propertyData, groupList);
            return {
                propertyConfig: [usual, formDef, process, time, advanced, extendProperty],
                propertyData: propertyData
            };
        } else {
            this.setProcessPropertyValue(propertyData, groupList);
            return {
                propertyConfig: [usual, formDef, process, time, advanced],
                propertyData: propertyData
            };
        }
    }

    updateProps(obj: any) {
        const propertyID = obj.propertyID;
        if (propertyID) {
            switch (propertyID) {
                case 'EntryForm'://流程分类
                    // 更新流程分类名称（流程分类名称可能修改）
                    this.updateProcessHeader(obj.propertyValue.name, obj.propertyValue.businessObjectId, obj.propertyValue.id);
                    this.updateFlowFormInfo(obj.formalParameters, obj.processBizComponnets, obj.executeForms, obj.customizeActions, obj.bizCmps);
                    this.bindProcessTitleExpression(obj.flowForm.processDescription);
                    break;
                case ProcessPropertyKeys.wf_Component: // 流程构件
                    this.updateProcessComponents(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.wf_ExecuteForms_PC:
                    this.updateExecuteForm(obj.propertyValue, 'PC');
                    if (obj.parameters && obj.parameters.length > 0) {
                        PropertyHelper.updateFormalParameters(this.process, obj.parameters);
                    }
                    break;
                case ProcessPropertyKeys.wf_ExecuteForms_Mobile:
                    this.updateExecuteForm(obj.propertyValue, 'MOBILE');
                    if (obj.parameters && obj.parameters.length > 0) {
                        PropertyHelper.updateFormalParameters(this.process, obj.parameters);
                    }
                    break;
                case ProcessPropertyKeys.wf_CustomizeAction:
                    this.updateCustomizeActions(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.wf_TitleExpression:
                    this.modifyProcessTitleExpression(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.allowPreAssign:
                    this.updateAllowPreAssign(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.startMode:
                    this.updateStartMode(obj.propertyValue, obj.allBizCmps, obj.bizCmps);
                    break;
                case ProcessPropertyKeys.enablePriority:
                    this.updateEnablePriority(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.enableWholeOrderRetrieve:
                    this.updateEnableWholeOrderRetrieve(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.processVariables:
                    this.updateProcessVariables(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.timeLimitControl:
                    this.updateProcessTimeLimitControl(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.messageNotice:
                    this.updateProcessMessageNotices(obj.propertyValue);
                    break;
                case ProcessPropertyKeys.imageSettings:
                    this.updateImageSettings(obj.propertyValue);
                    break;
                default:
                    // 判断ResourceKeys是不是groupid,是就走resetExtendProperty
                    if (this.editorUi.procExtProps.findIndex(p => p.id === propertyID) > -1) {
                        this.resetExtendProperty(obj.propertyValue);
                    } else {
                        super.updateProps(obj);
                    }
                    break;
            }
        }
    }

    /**
    * 设置流程属性值
    * @param bpmnModel
    * @param propertyData
    * @param groupList
    */
    setProcessPropertyValue(propertyData: any, groupList?: any): void {
        let process = this.process;
        if (process) {
            if (!process.Name)
                process.Name = '默认流程';
            if (!process.Key)
                process.Key = BpmnModelHelper.GenerateElementId();

            propertyData[ProcessPropertyKeys.Id] = process.Id;
            propertyData[ProcessPropertyKeys.ClrTypeID] = process.ClrTypeID;
            propertyData[ProcessPropertyKeys.Name] = process.Name;
            propertyData[ProcessPropertyKeys.NameLanguage] = process.NameLanguage || {
                "zh-CHS": process.Name,
            };
            propertyData[ProcessPropertyKeys.Key] = process.Key;
            propertyData[ProcessPropertyKeys.wf_TitleExpression] = '';

            let extendElements: ExtensionElement[] = process.GetExtensionElements();
            let component = [];
            let webForm;
            let mobileForm;
            let customizeActions = [];
            let proExtendProperties = [];
            let imageSettings;
            const messagesNotices = new Array();
            // tslint:disable-next-line: forin
            for (let i in extendElements) {
                if (extendElements[i] instanceof ProcessHeader) {
                    propertyData[ProcessPropertyKeys.EntryForm] = {
                        id: (extendElements[i] as ProcessHeader).BizDefKey,
                        name: (extendElements[i] as ProcessHeader).BizDefName
                    };
                    propertyData[ProcessPropertyKeys.allowPreAssign] = (extendElements[i] as ProcessHeader).allowPreAssign;
                    propertyData[ProcessPropertyKeys.startMode] = (extendElements[i] as ProcessHeader).startMode;
                    propertyData[ProcessPropertyKeys.enablePriority] = (extendElements[i] as ProcessHeader).enablePriority ? (extendElements[i] as ProcessHeader).enablePriority : false;
                    propertyData[ProcessPropertyKeys.enableWholeOrderRetrieve] = (extendElements[i] as ProcessHeader).enableWholeOrderRetrieve ? (extendElements[i] as ProcessHeader).enableWholeOrderRetrieve : false;
                }
                if (extendElements[i] instanceof Component
                    && (extendElements[i] as Component).Time !== ToolTime.UNDEFINITION) {
                    component.push(this.convert2Component(extendElements[i] as Component));
                }
                if (extendElements[i] instanceof ExecuteForm) {
                    const form = extendElements[i] as ExecuteForm;
                    if (form.TerminalType === 'PC') {
                        webForm = form;
                    } else if (form.TerminalType === 'MOBILE') {
                        mobileForm = form;
                    }
                }
                if (extendElements[i] instanceof TitleExpression) {
                    propertyData[ProcessPropertyKeys.wf_TitleExpression] =
                        (extendElements[i] as TitleExpression).valueLanguage ? (extendElements[i] as TitleExpression).valueLanguage : (extendElements[i] as TitleExpression).value;
                }
                if (extendElements[i] instanceof CustomizeAction) {
                    customizeActions.push(extendElements[i]);
                }
                if (extendElements[i] instanceof MessageNotice) {
                    messagesNotices.push(this.getMessageNotice(extendElements[i] as MessageNotice));
                }
                if (extendElements[i] instanceof TimeLimitControl) {
                    propertyData[ProcessPropertyKeys.timeLimitControl] = this.getPrcocessTimeLimitInfo(extendElements[i] as TimeLimitControl);
                }
                if (extendElements[i] instanceof ExtendProperty) {
                    proExtendProperties.push(extendElements[i]);
                }
                if (extendElements[i] instanceof ImageSettings) {
                    // imageSettings.push(extendElements[i]);
                    propertyData[ProcessPropertyKeys.imageSettings] = extendElements[i];
                }
            }
            const processVariables = this.getProcessVariables(process);
            propertyData[ProcessPropertyKeys.processVariables] = processVariables;
            //第一次打开设计器时会获取流程变量，这样打开后直接打开变量赋值，
            this.uiState.ProcessVariables = processVariables;
            propertyData[ProcessPropertyKeys.wf_Component] = component;
            propertyData[ProcessPropertyKeys.wf_ExecuteForms_PC] = webForm;
            propertyData[ProcessPropertyKeys.wf_ExecuteForms_Mobile] = mobileForm;
            propertyData[ProcessPropertyKeys.wf_CustomizeAction] = customizeActions;
            propertyData[ProcessPropertyKeys.messageNotice] = messagesNotices;
            for (const g of groupList) {
                // 取回模型上的value值给到扩展属性组件里
                if (proExtendProperties && proExtendProperties.length > 0) {
                    g.childs.sort((a, b) => {
                        return a.sortOrder - b.sortOrder;
                    }).forEach(d => {
                        proExtendProperties.forEach(e => {
                            if (e.key == d.code) {
                                d.value = e.value;
                            }
                        });
                    });
                }
                propertyData[g.parent.id] = g.childs;
            }
        }
    }

    getProcessVariables(process: Process): any[] {
        let processVariables = new Array();
        const extendElements: ExtensionElement[] = process.GetExtensionElements();
        const dataObjects = process.GetDataObjects();
        if (dataObjects !== null && dataObjects.length > 0) {
            for (const obj of dataObjects) {
                if (obj instanceof ValuedDataObject && obj.DataType.Category !== DataTypes.CustomType) {
                    const variable: any = {
                        id: obj.Id,
                        name: obj.Name,
                        documentation: obj.Documentation,
                        basicType: obj.DataType.BasicType,
                        tag: obj.variableSetting ? obj.variableSetting.tag : '',
                        value: obj.variableSetting ? obj.variableSetting.value : '',
                        valueText: obj.variableSetting ? obj.variableSetting.valueText : '',
                        description: obj.DataType.Description
                    };
                    if (obj.variableSetting && obj.variableSetting.tag === 'component') {
                        if (extendElements.length > 0) {
                            for (const e of extendElements) {
                                if (e instanceof Component && e.Id === obj.variableSetting.value) {
                                    const component: any = {
                                        Id: e.Id,
                                        Name: e.Name,
                                        MetadataId: e.MetadataId,
                                        MethodCode: e.MethodCode,
                                        ActualParameters: []
                                    };
                                    if (e.ActualParameters && e.ActualParameters.length > 0) {
                                        for (const p of e.ActualParameters) {
                                            const parameter = {
                                                Name: p.Name,
                                                Value: p.Value,
                                                Tag: p.Tag,
                                            };
                                            component.ActualParameters.push(parameter);
                                        }
                                    }
                                    variable.component = component;
                                }
                            }
                        }
                    }
                    processVariables.push(variable);
                }
            }
        }
        return processVariables;
    }

    getPrcocessTimeLimitInfo(timeLimitControl: TimeLimitControl): TimeLimitControlInfo {
        let timeLimit;
        if (timeLimitControl) {
            timeLimit = new TimeLimitControlInfo();
            timeLimit.enable = timeLimitControl.enable;
            timeLimit.tag = timeLimitControl.tag;
            timeLimit.dueTime = timeLimitControl.dueTime;
            timeLimit.timeUnit = timeLimitControl.timeUnit;
            timeLimit.expireOperations = new Array();
            if (timeLimitControl.expireOperations && timeLimitControl.expireOperations.length > 0) {
                const operations = timeLimitControl.expireOperations;
                for (const o of operations) {
                    if (o.ClrTypeID.indexOf('SendMessageExpireOperation') > 0) {
                        const operation = o as SendMessageExpireOperation;
                        const sendMessage = new SendMessageExpireOperationInfo();
                        sendMessage.timeDuration = operation.timeDuration;
                        sendMessage.durationType = operation.durationType;
                        sendMessage.timeUnit = operation.timeUnit;
                        sendMessage.message = operation.message;
                        sendMessage.sendType = operation.sendType;
                        sendMessage.subject = operation.subject;
                        sendMessage.id = operation.id;
                        sendMessage.receiver = operation.receiver;
                        sendMessage.receiverText = operation.receiverText;
                        sendMessage.cycleEnable = operation.cycleEnable;
                        sendMessage.cycleTime = operation.cycleTime;
                        sendMessage.cycleTimeUnit = operation.cycleTimeUnit;
                        sendMessage.operationTypeName = operation.operationTypeName ? operation.operationTypeName : 'SendMessageExpireOperation';
                        if (operation.smsMessageTemplate) {
                            const smsTemplate: any = new Object;
                            smsTemplate.code = operation.smsMessageTemplate.code;
                            smsTemplate.content = operation.smsMessageTemplate.content;
                            smsTemplate.smsMessageVariables = new Array();
                            if (operation.smsMessageTemplate.smsMessageVariables && operation.smsMessageTemplate.smsMessageVariables.length > 0) {
                                for (const v of operation.smsMessageTemplate.smsMessageVariables) {
                                    const variable: any = new Object;
                                    variable.id = WfDesignerUtils.createNM();
                                    variable.code = v.code;
                                    variable.value = v.value;
                                    variable.tag = v.tag;
                                    smsTemplate.smsMessageVariables.push(variable);
                                }
                            }
                            sendMessage.smsMessageTemplate = smsTemplate;
                        }
                        timeLimit.expireOperations.push(sendMessage);
                    } else if (o.ClrTypeID.indexOf('AutoHandleExpireOperation') > 0) {
                        const operation = o as AutoHandleExpireOperation;
                        const autoHandle = new AutoHandleExpireOperationInfo;
                        autoHandle.id = operation.id;
                        autoHandle.durationType = operation.durationType;
                        autoHandle.timeDuration = operation.timeDuration;
                        autoHandle.timeUnit = operation.timeUnit;
                        autoHandle.operationTypeName = operation.operationTypeName ? operation.operationTypeName : 'AutoHandleExpireOperation';
                        autoHandle.autoHandleType = operation.autoHandleType;
                        autoHandle.message = operation.message;
                        timeLimit.expireOperations.push(autoHandle);
                    }
                }
            }
        }
        return timeLimit;
    }

    getMessageNotice(messageNotice: MessageNotice) {
        const message: any = new Object;
        message.content = messageNotice.content;
        message.sendTime = messageNotice.sendTime;
        message.sendType = messageNotice.sendType;
        message.subject = messageNotice.subject;
        if (messageNotice.smsMessageTemplate) {
            const smsTemplate: any = new Object;
            smsTemplate.code = messageNotice.smsMessageTemplate.code;
            smsTemplate.content = messageNotice.smsMessageTemplate.content;
            smsTemplate.smsMessageVariables = new Array();
            if (messageNotice.smsMessageTemplate.smsMessageVariables && messageNotice.smsMessageTemplate.smsMessageVariables.length > 0) {
                for (const v of messageNotice.smsMessageTemplate.smsMessageVariables) {
                    const variable: any = new Object;
                    variable.id = WfDesignerUtils.createNM();
                    variable.code = v.code;
                    variable.value = v.value;
                    variable.tag = v.tag;
                    smsTemplate.smsMessageVariables.push(variable);
                }
            }
            message.smsMessageTemplate = smsTemplate;
        }
        const receiverExpressions = new Array<MessageReceiver>();
        if (messageNotice.receiver) {
            const receivers = messageNotice.receiver.split(',');
            const receiverTexts = messageNotice.receiverText.split(',');
            let userReceiver = new MessageReceiver();
            userReceiver.type = 'userParticipant';
            userReceiver.userIds = '';
            userReceiver.userNames = '';
            for (const i in receivers) {
                if (receivers[i] === 'procInstCreator') {
                    let procInstCreator = new MessageReceiver()
                    procInstCreator.type = 'processParticipant';
                    procInstCreator.processParticipant = 'procInstCreator';
                    receiverExpressions.push(procInstCreator);
                } else if (receivers[i] === 'workItemParticipant') {
                    let workItemParticipant = new MessageReceiver()
                    workItemParticipant.type = 'processParticipant';
                    workItemParticipant.processParticipant = 'workItemParticipant';
                    receiverExpressions.push(workItemParticipant);
                } else {
                    userReceiver.userIds += (receivers[i] + ',');
                    userReceiver.userNames += (receiverTexts[i] + ',');
                }
            }
            if (userReceiver.userIds) {
                userReceiver.userIds = userReceiver.userIds.substring(0, userReceiver.userIds.length - 1);
                userReceiver.userNames = userReceiver.userNames.substring(0, userReceiver.userNames.length - 1);
                receiverExpressions.push(userReceiver);
            }
        }
        if (messageNotice.receiverExpressions && messageNotice.receiverExpressions.length > 0) {
            for (const e of messageNotice.receiverExpressions) {
                if (e instanceof ActivityExecutorParticipantExpression) {
                    let activityExecutorParticipant = new MessageReceiver();
                    activityExecutorParticipant.type = 'processParticipant';
                    activityExecutorParticipant.processParticipant = 'activityExecutorParticipant';
                    activityExecutorParticipant.activityDefId = (e as ActivityExecutorParticipantExpression).activityDefId;
                    activityExecutorParticipant.activityDefName = (e as ActivityExecutorParticipantExpression).activityDefName;

                    receiverExpressions.push(activityExecutorParticipant);
                } else if (e instanceof UserParticipantExpression) {
                    let variableReceiver = new MessageReceiver();
                    variableReceiver.type = 'variableParticipant';
                    variableReceiver.variableParticipant = (e as UserParticipantExpression).UserId;
                    variableReceiver.variableName = (e as UserParticipantExpression).name;
                    variableReceiver.orgType = (e as UserParticipantExpression).OrgType;
                    receiverExpressions.push(variableReceiver);
                } else if (e instanceof PositionParticipantExpression) {
                    let positionReceiver = new MessageReceiver();
                    positionReceiver.type = 'positionParticipant';
                    positionReceiver.positionId = (e as PositionParticipantExpression).PositionId;
                    positionReceiver.positionName = (e as PositionParticipantExpression).PositionName;
                    positionReceiver.positionType = (e as PositionParticipantExpression).PositionType;
                    if (positionReceiver.positionType === '1') {
                        positionReceiver.orgId = (e as PositionParticipantExpression).OrgId;
                        positionReceiver.orgType = (e as PositionParticipantExpression).OrgType;
                    }

                    receiverExpressions.push(positionReceiver);
                }
            }
        }
        if (receiverExpressions.length > 0) {
            message.receivers = receiverExpressions;
        }
        return message;

    }

    convert2Component(component: Component): BizComponent {
        const bizComponent = new BizComponent();
        bizComponent.id = component.Id;
        bizComponent.name = component.Name;
        bizComponent.metadataId = component.MetadataId;
        bizComponent.methodCode = component.MethodCode;
        bizComponent.executionTime = component.Time;
        bizComponent.isForCompensation = component.isForCompensation;
        bizComponent.scopeType = component.ScopeType || null;
        bizComponent.actualParameters = new Array();
        if (component.ActualParameters && component.ActualParameters.length > 0) {
            for (const param of component.ActualParameters) {
                const bizParam = new BizParameter();
                bizParam.code = param.Name;
                bizParam.name = param.Name;
                bizParam.value = param.Value;
                bizComponent.actualParameters.push(bizParam);
            }
        }
        return bizComponent;
    }

    /**
     * 设置ProcessHeader
     * @param ffId
     * @param bizObjId
     * @param ffName
     * @param startMode
     */
    setProcessHeader(ffId: string, bizObjId: string, ffName: string, startMode: StartMode) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        const processHeader = new ProcessHeader(process.Model, ffId, bizObjId, ffName, startMode);
        extendElements.push(processHeader);
    }

    /**
     * 更新流程分类绑定的参数，流程组件，表单信息
     * @param params 参数列表
     * @param cpts 流程组件
     * @param form 流程表单
     */
    updateFlowFormInfo(params: any[], cpts: any[], forms: any[], customizeActions: any[], bizCmps: any) {
        this.updateFormalParameters(params, cpts.filter(cpt => cpt.ownerType === 'Schema'));
        this.setProcessComponents(cpts, bizCmps);
        this.updateExecuteForms(forms);
        this.updateCustomizeActions(customizeActions);
    }

    updateFormalParameters(parameters: any[], cmps: any[] = new Array()) {
        if (!parameters) {
            parameters = [];
        }
        let process = this.process;
        let objects = process.GetDataObjects();
        if (parameters !== null && parameters.length > 0) {
            for (const p of parameters) {
                const dataType = new DataType(process.Model);
                if (p.dataType === 'CDTSchema') {
                    // 先清理CDTSchema类型的dataObject
                    for (const o of objects) {
                        //TODO:数据类型为customType暂时默认为参数预制
                        if (o instanceof DataObject && o.DataType.Category === DataTypes.CustomType) {
                            process.RemoveDataObject(o.Id);
                        }
                    }
                    // 重新绑定流程分类支持精简be
                    const customType = new CDTSchemaType(process.Model);
                    if (p.newSchemaId && p.newSchemaMetadataId) {
                      customType.SchemaId = p.newSchemaId;
                      customType.MetadataId = p.newSchemaMetadataId;
                    } else {
                      customType.SchemaId = p.schemaId;
                      customType.MetadataId = p.schemaMetadataId;
                    }
                    customType.ComponentId = cmps.filter(cmp => cmp.owner === p.schemaId)[0].id;
                    dataType.Category = DataTypes.CustomType;
                    dataType.CustomType = customType;
                } else {
                    // 先清理code相同的dataObject
                    for (const o of objects) {
                        if (o instanceof DataObject && o.Name === p.code) {
                            process.RemoveDataObject(o.Id);
                        }
                    }
                    dataType.Category = DataTypes.BasicType;
                    dataType.BasicType = p.dataType;
                }
                dataType.Description = p.description;
                // dataType.InitialValue = p.initialValue;
                if (p.parameterMode === 0) {
                    const dataObject = new ValuedDataObject(process.Model);
                    dataObject.Name = p.code;
                    dataObject.Documentation = p.name;
                    dataObject.DataType = dataType;
                    dataObject.IsAllowNull = p.allowNull;
                    if (p.initialValue || p.initialValueType) {
                        const variableSetting = new VariableSetting(process.Model);
                        variableSetting.variableCode = p.code;
                        variableSetting.variableName = p.name;
                        variableSetting.tag = p.initialValueType;
                        variableSetting.value = p.initialValue;
                        variableSetting.valueText = p.initialValue;

                        dataObject.variableSetting = variableSetting;
                    }

                    process.AddDataObject(dataObject);
                }
            }
        }
    }


    /**
     * 基于流程分类的切换更新流程构件
     */
    setProcessComponents(procCpts: any[], bizCmps: any) {
        const process = this.process;
        let processHeader: ProcessHeader;
        let extendElements = process.GetExtensionElements();
        //删除流程构件
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof Component
                    && (extendElements[i - 1] as Component).Time !== ToolTime.UNDEFINITION) {
                    extendElements.splice(i - 1, 1);
                }
                if (extendElements[i - 1] instanceof ProcessHeader) {
                    processHeader = extendElements[i - 1] as ProcessHeader;
                }
            }
        }
        // 子流程和外部流程的情况先需要绑定取数构件
        if (processHeader.startMode !== StartMode.DIRECT) {
            const loadDataComponent = procCpts.find(p => p.executionTime === ToolTime.LOADDATA);
            if (loadDataComponent) {
                let ldCpt = new Component(process.Model);
                ldCpt.Time = loadDataComponent.executionTime as ToolTime;
                ldCpt.MetadataId = loadDataComponent.metadataId;
                ldCpt.MethodCode = loadDataComponent.methodCode;
                ldCpt.Id = loadDataComponent.id;
                ldCpt.Name = loadDataComponent.name;
                ldCpt.isForCompensation = loadDataComponent.isForCompensation;
                ldCpt.ActualParameters = [];
                if (loadDataComponent.scopeType) {
                    ldCpt.ScopeType = loadDataComponent.scopeType;
                } else {
                    ldCpt.ScopeType = "Undefined"
                }
                if (loadDataComponent.actualParameters && loadDataComponent.actualParameters.length > 0) {
                    for (let item of loadDataComponent.actualParameters) {
                        let parameter = new ActualParameter(process.Model, item.code, item.value);

                        ldCpt.ActualParameters.push(parameter);
                    }
                }
                // 把取数构件放进去
                extendElements.push(ldCpt);
                // 如果是外部流程，校验是否集成统一预制的外部流程构件
                if (processHeader.startMode == StartMode.EXTERNAL_PROCESS) {
                    let externalCmps = false;
                    const cmps = bizCmps;
                    if (cmps && cmps.length > 0) {
                        // 此处标记有外部流程通用构件
                        for (const c of cmps) {
                            if (c.scopeType === 'ExternalProcess') {
                                externalCmps = true;
                                break;
                            }
                        }
                    }
                    // 如果没有外部流程构件
                    if (!externalCmps) {
                        if (procCpts && procCpts.length > 0) {
                            for (let procCpt of procCpts) {
                                let cpt = new Component(process.Model);
                                cpt.Time = procCpt.executionTime as ToolTime;
                                cpt.MetadataId = procCpt.metadataId;
                                cpt.MethodCode = procCpt.methodCode;
                                cpt.Id = procCpt.id;
                                cpt.Name = procCpt.name;
                                cpt.isForCompensation = procCpt.isForCompensation;
                                cpt.ActualParameters = [];
                                if (procCpt.scopeType) {
                                    cpt.ScopeType = procCpt.scopeType;
                                } else {
                                    cpt.ScopeType = "Undefined"
                                }
                                if (procCpt.actualParameters && procCpt.actualParameters.length > 0) {
                                    for (let item of procCpt.actualParameters) {
                                        let parameter = new ActualParameter(process.Model, item.code, item.value);
                                        cpt.ActualParameters.push(parameter);
                                    }
                                }
                                // 避免重复的放入取数构件,不要取数，子流程和直接流程的构件，要未定义，外部，全量的
                                if (cpt.Time !== ToolTime.LOADDATA
                                    && cpt.ScopeType !== StartMode.DIRECT
                                    && cpt.ScopeType !== StartMode.SUB_PROCESS
                                ) {
                                    extendElements.push(cpt);
                                }
                            }
                        }
                    } else {
                        for (let procCpt of procCpts) {
                            let cpt = new Component(process.Model);
                            cpt.Time = procCpt.executionTime as ToolTime;
                            cpt.MetadataId = procCpt.metadataId;
                            cpt.MethodCode = procCpt.methodCode;
                            cpt.Id = procCpt.id;
                            cpt.Name = procCpt.name;
                            cpt.isForCompensation = procCpt.isForCompensation;
                            cpt.ActualParameters = [];
                            if (procCpt.scopeType) {
                                cpt.ScopeType = procCpt.scopeType;
                            } else {
                                cpt.ScopeType = "Undefined"
                            }
                            if (procCpt.actualParameters && procCpt.actualParameters.length > 0) {
                                for (let item of procCpt.actualParameters) {
                                    let parameter = new ActualParameter(process.Model, item.code, item.value);
                                    cpt.ActualParameters.push(parameter);
                                }
                            }
                            // 避免重复的放入取数构件,不要取数，子流程和直接流程,未知的构件，外部，全量的
                            if (cpt.Time !== ToolTime.LOADDATA
                                && cpt.ScopeType !== StartMode.DIRECT
                                && cpt.ScopeType !== StartMode.SUB_PROCESS
                                && cpt.ScopeType !== "Undefined"
                            ) {
                                extendElements.push(cpt);
                            }
                        }
                    }
                    // 如果有外部流程构件，同样只保留取数构件，不需要其他处理(取数构件已经放进去了)
                    // 刷新一下属性框
                    this.editorUi.fireEvent(new mxEventObject(PmfEvents.ShowProperty));
                } else if (processHeader.startMode == StartMode.SUB_PROCESS) {
                    if (procCpts && procCpts.length > 0) {
                        for (let procCpt of procCpts) {
                            let cpt = new Component(process.Model);
                            cpt.Time = procCpt.executionTime as ToolTime;
                            cpt.MetadataId = procCpt.metadataId;
                            cpt.MethodCode = procCpt.methodCode;
                            cpt.Id = procCpt.id;
                            cpt.Name = procCpt.name;
                            cpt.isForCompensation = procCpt.isForCompensation;
                            cpt.ActualParameters = [];
                            if (procCpt.scopeType) {
                                cpt.ScopeType = procCpt.scopeType;
                            } else {
                                cpt.ScopeType = "Undefined"
                            }
                            if (procCpt.actualParameters && procCpt.actualParameters.length > 0) {
                                for (let item of procCpt.actualParameters) {
                                    let parameter = new ActualParameter(process.Model, item.code, item.value);
                                    cpt.ActualParameters.push(parameter);
                                }
                            }
                            // 避免重复的放入取数构件,不要取数，直接流程和外部流程，未定义的构件，要子流程，全量的
                            if (cpt.Time !== ToolTime.LOADDATA
                                && cpt.ScopeType !== StartMode.DIRECT
                                && cpt.ScopeType !== StartMode.EXTERNAL_PROCESS
                                && cpt.ScopeType !== "Undefined") {
                                extendElements.push(cpt);
                            }
                        }
                    }
                }
                // 如果是子流程则不需要其他处理(取数构件已经放进去了)
            }
        } else { // 直接流程
            // 直接流程过滤掉共享的构件
            const actualCmps = procCpts.filter(p => WfDesignerUtils.isPfComponent(p.metadataId) === false);
            if (actualCmps && actualCmps.length > 0) {
                for (let procCpt of actualCmps) {
                    let cpt = new Component(process.Model);
                    cpt.Time = procCpt.executionTime as ToolTime;
                    cpt.MetadataId = procCpt.metadataId;
                    cpt.MethodCode = procCpt.methodCode;
                    cpt.Id = procCpt.id;
                    cpt.Name = procCpt.name;
                    cpt.isForCompensation = procCpt.isForCompensation;
                    if (procCpt.scopeType) {
                        cpt.ScopeType = procCpt.scopeType;
                    } else {
                        cpt.ScopeType = "Undefined"
                    }
                    cpt.ActualParameters = [];
                    if (procCpt.actualParameters && procCpt.actualParameters.length > 0) {
                        for (let item of procCpt.actualParameters) {
                            let parameter = new ActualParameter(process.Model, item.code, item.value);

                            cpt.ActualParameters.push(parameter);
                        }
                    }
                    // 避免重复的放入取数构件,子流程和外部流程的构件，要未定义，直接，全量的
                    if (cpt.ScopeType !== StartMode.EXTERNAL_PROCESS
                        && cpt.ScopeType !== StartMode.SUB_PROCESS) {
                        extendElements.push(cpt);
                    }
                }
            }
        }
    }

    updateExecuteForms(forms: any[]) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        //删除表单构件
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof ExecuteForm) {
                    extendElements.splice(i - 1, 1);
                }
            }
        }
        for (const f of forms) {
            this.updateExecuteForm(f);
        }
    }
    updateExecuteForm(form: any, terminal?: string) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        //删除表单构件
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof ExecuteForm) {
                    const f = extendElements[i - 1] as ExecuteForm;
                    if (f.TerminalType === (terminal ? terminal : form.TerminalType)) {
                        extendElements.splice(i - 1, 1);
                    }
                }
            }
        }
        if (form) {
            let params = form.ActualParameters as any[];
            let actParas = new Array<ActualParameter>();
            if (params && params.length > 0) {
                for (let para of params) {
                    let actPara = new ActualParameter(process.Model, para.Name, para.Value);
                    if (para.Tag) {
                        actPara.Tag = para.Tag;
                    } else {
                        if (para.Value) {
                            if (para.Value.length >= 8 && para.Value.slice(0, 8) === `{"expr":`) {
                                actPara.Tag = 'expression';
                            } else {
                                actPara.Tag = 'constant';
                            }
                        } else {
                            actPara.Tag = 'variable';
                        }

                    }
                    actParas.push(actPara);
                }
            }
            const id = BpmnModelHelper.GenerateElementId();
            let item = new ExecuteForm(process.Model, id, form.Name, actParas);
            item.ComponentId = form.ComponentId;
            item.FormType = form.FormType;
            item.formFormatId = form.formFormatId;
            item.formatConfigId = form.formatConfigId;
            item.TerminalType = form.TerminalType;
            extendElements.push(item);
        }
    }

    updateCustomizeActions(customizeActions: any[]) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        //删除流程构件
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof CustomizeAction) {

                    extendElements.splice(i - 1, 1);
                }
            }
        }
        //重新绑定
        if (customizeActions && customizeActions.length > 0) {
            for (let a of customizeActions) {
                let action = new CustomizeAction(process.Model);
                action.id = BpmnModelHelper.GenerateElementId();
                action.code = a.code;
                action.name = a.name;
                action.terminalType = a.terminalType;
                // action.enable = a.enable;
                action.actualParameters = [];
                if (a.actualParameters && a.actualParameters.length > 0) {
                    for (let p of a.actualParameters) {
                        let parameter = new ActualParameter(process.Model, p.Name, p.Value, p.ValueText, p.Tag || 'expression');
                        action.actualParameters.push(parameter);
                    }
                }

                extendElements.push(action);
            }
        }
    }

    /**
     * 更新流程备注
     * @param bpmnModel
     * @param value
     */
    modifyProcessTitleExpression(value: any) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof TitleExpression) {

                    extendElements.splice(i - 1, 1);
                }
            }
        }
        let title = new TitleExpression(process.Model);
        if(typeof(value) === 'string'|| value == null) {
          title = new TitleExpression(process.Model, value);
        } else {
          title = new TitleExpression(process.Model, value['zh-CHS'], value);
        }
        extendElements.push(title);
    }

    updateProcessHeader(ffName: string, bizObjId?: string, ffId?: string) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements !== null && extendElements.length > 0) {
            for (let i in extendElements) {
                if (extendElements[i] instanceof ProcessHeader) {
                    (extendElements[i] as ProcessHeader).BizDefName = ffName;
                    (extendElements[i] as ProcessHeader).BizObjId = bizObjId;
                    (extendElements[i] as ProcessHeader).BizDefKey = ffId;
                    return;
                }
            }
        }
    }

    bindProcessTitleExpression(value: string) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof TitleExpression) {

                    extendElements.splice(i - 1, 1);
                }
            }
        }
        const title = new TitleExpression(process.Model, value);
        extendElements.push(title);
    }

    /**
     * 基于流程分类的切换更新流程构件
     */
    updateProcessComponents(procCpts: any[]) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        //删除流程构件
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof Component
                    && (extendElements[i - 1] as Component).Time !== ToolTime.UNDEFINITION) {
                    extendElements.splice(i - 1, 1);
                }
            }
        }
        //重新绑定
        if (procCpts && procCpts.length > 0) {
            for (let procCpt of procCpts) {
                let cpt = new Component(process.Model);
                cpt.Time = procCpt.executionTime as ToolTime;
                cpt.MetadataId = procCpt.metadataId;
                cpt.MethodCode = procCpt.methodCode;
                cpt.Id = procCpt.id;
                cpt.Name = procCpt.name;
                cpt.isForCompensation = procCpt.isForCompensation;
                cpt.ActualParameters = [];
                if (procCpt.actualParameters && procCpt.actualParameters.length > 0) {
                    for (let item of procCpt.actualParameters) {
                        let parameter = new ActualParameter(process.Model, item.code, item.value);

                        cpt.ActualParameters.push(parameter);
                    }
                }

                extendElements.push(cpt);
            }
        }
    }

    updateAllowPreAssign(assign: boolean) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements !== null && extendElements.length > 0) {
            for (let i in extendElements) {
                if (extendElements[i] instanceof ProcessHeader) {
                    (extendElements[i] as ProcessHeader).allowPreAssign = assign;
                    return;
                }
            }
        }
    }

    updateEnablePriority(priority: boolean) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements !== null && extendElements.length > 0) {
            for (let i in extendElements) {
                if (extendElements[i] instanceof ProcessHeader) {
                    (extendElements[i] as ProcessHeader).enablePriority = priority;
                    return;
                }
            }
        }
    }

    updateStartMode(startMode: StartMode, allCmps: any, bizCmps: any) {
        // 对于启动方式的提示信息
        switch (startMode) {
            case StartMode.DIRECT:
                this.editorUi.fireEvent(new mxEventObject(WfEvents.ShowTips, "data", {
                    type: 'message',
                    level: 'info',
                    message:  mxResources.get("directTips"),
                }));
                break;
            case StartMode.EXTERNAL_PROCESS:
                this.editorUi.fireEvent(new mxEventObject(WfEvents.ShowTips, "data", {
                    type: 'message',
                    level: 'info',
                    message:  mxResources.get("externalTips"),
                }));
                break;
            case StartMode.SUB_PROCESS:
                this.editorUi.fireEvent(new mxEventObject(WfEvents.ShowTips, "data", {
                    type: 'message',
                    level: 'info',
                    message:  mxResources.get("subprocessTips"),
                }));
                break;
        }
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements !== null && extendElements.length > 0) {
            const procHeader = extendElements.filter(e => e instanceof ProcessHeader) as ProcessHeader[];
            // 修改启动方式
            procHeader[0].startMode = startMode;

            const procCpts = allCmps;
            //删除流程构件
            if (extendElements.length > 0) {
                for (let i = extendElements.length; i > 0; i--) {
                    if (extendElements[i - 1] instanceof Component
                        && (extendElements[i - 1] as Component).Time !== ToolTime.UNDEFINITION
                        && (extendElements[i - 1] as Component).Time !== ToolTime.LOADDATA) {
                        extendElements.splice(i - 1, 1);
                    }
                }
            }

            allCmps && allCmps.forEach(procCpt => {
                const cpt = new Component(process.Model);
                cpt.Time = procCpt.executionTime as ToolTime;
                cpt.MetadataId = procCpt.metadataId;
                cpt.MethodCode = procCpt.methodCode;
                cpt.Id = procCpt.id;
                cpt.Name = procCpt.name;
                cpt.isForCompensation = procCpt.isForCompensation;
                cpt.ActualParameters = [];
                if (procCpt.scopeType) {
                    cpt.ScopeType = procCpt.scopeType;
                } else {
                    cpt.ScopeType = "Undefined"
                }

                procCpt.actualParameters && procCpt.actualParameters.forEach((item) => {
                    let parameter = new ActualParameter(process.Model, item.code, item.value);
                    cpt.ActualParameters.push(parameter);
                });

                extendElements.push(cpt);
            });

            const procCmps = extendElements.filter(e => e instanceof Component) as Component[];
            // 如果启动方式是子流程,只保留取数构件
            if (startMode === StartMode.SUB_PROCESS) {
                // Utils.postMessage(ResourceKeys.wf_showTip, {
                //     type: 'message',
                //     level: 'info',
                //     message: `启动方式为子流程，需要移除取数构件以外的流程构件`,
                // });
                for (let i = extendElements.length; i > 0; i--) {
                    if (extendElements[i - 1] instanceof Component) {
                        const cmp = extendElements[i - 1] as Component;
                        if (
                            cmp.Time !== ToolTime.UNDEFINITION
                            && cmp.Time !== ToolTime.LOADDATA
                            && (cmp.ScopeType !== StartMode.SUB_PROCESS && cmp.ScopeType !== "All_Process")
                        ) {
                            extendElements.splice(i - 1, 1);
                        }
                    }
                }
            }
            // 如果启动方式是直接启动,不允许存在共享的构件
            if (startMode === StartMode.DIRECT) {
                // 判断有没有共享（PF）的流程构件
                const pfCmps = procCmps.filter((p: Component) => WfDesignerUtils.isPfComponent(p.MetadataId));
                if (pfCmps && pfCmps.length > 0) {
                    // 删掉共享的构件和启动方式
                    for (let i = extendElements.length; i > 0; i--) {
                        if (extendElements[i - 1] instanceof Component) {
                            const cmp = extendElements[i - 1] as Component;
                            if (
                                WfDesignerUtils.isPfComponent(cmp.MetadataId)
                                || cmp.ScopeType === StartMode.EXTERNAL_PROCESS
                                || cmp.ScopeType === StartMode.SUB_PROCESS
                            ) {
                                extendElements.splice(i - 1, 1);
                            }
                        }
                    }
                }else {
                    for (let i = extendElements.length; i > 0; i--) {
                        if (extendElements[i - 1] instanceof Component) {
                            const cmp = extendElements[i - 1] as Component;
                            if (cmp.ScopeType === StartMode.EXTERNAL_PROCESS
                                || cmp.ScopeType === StartMode.SUB_PROCESS
                            ) {
                                extendElements.splice(i - 1, 1);
                            }
                        }
                    }
                }
                // 如果启动方式是外部流程
            } else if (startMode === StartMode.EXTERNAL_PROCESS) {
                // const otherCmps = procCmps.filter((p: Component) => (p.MetadataId !== ResourceKeys.pf_Cmp_Id1
                //     && p.MetadataId !== ResourceKeys.pf_Cmp_Id2
                //     && p.MetadataId !== ResourceKeys.pf_Cmp_Id3
                //     && p.MetadataId !== ResourceKeys.pf_Cmp_Id4
                //     && p.Time !== ToolTime.LOADDATA
                // ));
                // 判断有没有预制外部流程通用构件
                let externalCmps = false;
                const cmps = bizCmps;
                if (cmps && cmps.length > 0) {
                    // 此处标记是外部流程通用构件
                    for (const c of cmps) {
                        if (c.scopeType === 'ExternalProcess') {
                            externalCmps = true;
                            break;
                        }
                    }
                }
                // 如果预制了外部流程通用构件,只保留取数构件，没有则不处理
                if (externalCmps) {
                    for (let i = extendElements.length; i > 0; i--) {
                        if (extendElements[i - 1] instanceof Component) {
                            const cmp = extendElements[i - 1] as Component;
                            if (cmp.Time !== ToolTime.UNDEFINITION
                                && cmp.Time !== ToolTime.LOADDATA
                                && (cmp.ScopeType === StartMode.DIRECT
                                    || cmp.ScopeType === StartMode.SUB_PROCESS
                                    || cmp.ScopeType === "Undefined")) {
                                extendElements.splice(i - 1, 1);
                            }
                        }
                    }
                }else {
                    for (let i = extendElements.length; i > 0; i--) {
                        if (extendElements[i - 1] instanceof Component) {
                            const cmp = extendElements[i - 1] as Component;
                            if (cmp.Time !== ToolTime.UNDEFINITION
                                && cmp.Time !== ToolTime.LOADDATA
                                && (cmp.ScopeType === StartMode.DIRECT
                                    || cmp.ScopeType === StartMode.SUB_PROCESS)) {
                                extendElements.splice(i - 1, 1);
                            }
                        }
                    }  
                }
            }
        }
        this.editorUi.fireEvent(new mxEventObject(PmfEvents.ShowProperty));
    }

    updateProcessVariables(variables: any[]) {
        let process = this.process;
        let objects = process.GetDataObjects();
        const extendElements = process.GetExtensionElements();
        if (objects && objects.length > 0) {
            for (const o of objects) {
                if (o instanceof ValuedDataObject && o.DataType.Category !== DataTypes.CustomType) {
                    if (o.variableSetting && o.variableSetting.tag === 'component') {
                        const i = extendElements.findIndex(e => (e instanceof Component && e.Id === o.variableSetting.value));
                        extendElements.splice(i, 1);
                    }
                    process.RemoveDataObject(o.Id);
                }

            }
        }

        if (variables !== null && variables.length > 0) {
            for (const v of variables) {
                const dataType = new DataType(process.Model);
                dataType.Category = DataTypes.BasicType;
                dataType.BasicType = v.basicType;
                dataType.Description = v.description;
                const dataObject = new ValuedDataObject(process.Model);
                dataObject.Id = v.id;
                dataObject.Name = v.name;
                dataObject.Documentation = v.documentation;
                dataObject.DataType = dataType;

                if (v.tag && v.value) {
                    const variableSetting = new VariableSetting(process.Model);
                    variableSetting.variableCode = v.name;
                    variableSetting.variableName = v.documentation;
                    variableSetting.tag = v.tag;
                    variableSetting.value = v.value;
                    variableSetting.valueText = v.valueText;

                    dataObject.variableSetting = variableSetting;

                    if (variableSetting.tag === 'component') {
                        const c = v.component;
                        if (c) {
                            let cpt = new Component(process.Model);
                            cpt.MetadataId = c.MetadataId;
                            cpt.MethodCode = c.MethodCode;
                            cpt.Id = c.Id;
                            cpt.Name = c.Name;
                            cpt.ActualParameters = new Array();
                            if (c.ActualParameters && c.ActualParameters.length > 0) {
                                for (let a of c.ActualParameters) {
                                    let parameter = new ActualParameter(process.Model, a.Name, a.Value);
                                    parameter.Tag = a.Tag;
                                    cpt.ActualParameters.push(parameter);
                                }
                            }

                            extendElements.push(cpt);
                        }
                    }
                }

                process.AddDataObject(dataObject);
            }
        }
    }

    updateProcessTimeLimitControl(timeLimit: any) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof TimeLimitControl) {
                    extendElements.splice(i - 1, 1);
                }
            }
        }
        const timeLimitInfo = timeLimit as TimeLimitControlInfo;
        if (timeLimitInfo) {
            const control = new TimeLimitControl(process.Model);
            control.enable = timeLimitInfo.enable;
            control.tag = timeLimitInfo.tag;
            control.dueTime = timeLimitInfo.dueTime;
            control.timeUnit = timeLimitInfo.timeUnit;
            control.expireOperations = new Array<SendMessageExpireOperation>();
            if (timeLimitInfo.expireOperations && timeLimitInfo.expireOperations.length > 0) {
                for (const o of timeLimitInfo.expireOperations) {
                    if (o.operationTypeName === 'SendMessageExpireOperation' || !o.operationTypeName) {
                        const operation = o as SendMessageExpireOperation;
                        const sendMessage = new SendMessageExpireOperation(process.Model);
                        sendMessage.id = BpmnModelHelper.GenerateElementId();
                        sendMessage.durationType = operation.durationType as DurationType;
                        sendMessage.timeDuration = operation.timeDuration;
                        sendMessage.timeUnit = operation.timeUnit;
                        sendMessage.operationTypeName = operation.operationTypeName ? operation.operationTypeName : 'SendMessageExpireOperation';
                        sendMessage.message = operation.message;
                        sendMessage.subject = operation.subject;
                        sendMessage.sendType = operation.sendType;
                        sendMessage.receiver = operation.receiver;
                        sendMessage.receiverText = operation.receiverText;
                        sendMessage.cycleEnable = operation.cycleEnable;
                        sendMessage.cycleTime = operation.cycleTime;
                        sendMessage.cycleTimeUnit = operation.cycleTimeUnit;
                        if (operation.smsMessageTemplate) {
                            const smsMessageTemplate = new SmsMessageTemplate(process.Model);
                            smsMessageTemplate.code = operation.smsMessageTemplate.code;
                            smsMessageTemplate.content = operation.smsMessageTemplate.content;
                            smsMessageTemplate.smsMessageVariables = new Array();
                            if (operation.smsMessageTemplate.smsMessageVariables && operation.smsMessageTemplate.smsMessageVariables.length > 0) {
                                for (const v of operation.smsMessageTemplate.smsMessageVariables) {
                                    const variable = new SmsMessageVariable(process.Model);
                                    variable.code = v.code;
                                    variable.value = v.value;
                                    variable.tag = v.tag;
                                    smsMessageTemplate.smsMessageVariables.push(variable);
                                }
                            }
                            sendMessage.smsMessageTemplate = smsMessageTemplate;
                        }
                        control.expireOperations.push(sendMessage);
                    } else if (o.operationTypeName === 'AutoHandleExpireOperation') {
                        const operation = o as AutoHandleExpireOperation;
                        const autoHandle = new AutoHandleExpireOperation(process.Model);
                        autoHandle.id = BpmnModelHelper.GenerateElementId();
                        autoHandle.durationType = operation.durationType as DurationType;
                        autoHandle.timeDuration = operation.timeDuration;
                        autoHandle.timeUnit = operation.timeUnit;
                        autoHandle.operationTypeName = operation.operationTypeName ? operation.operationTypeName : 'AutoHandleExpireOperation';
                        autoHandle.autoHandleType = operation.autoHandleType;
                        autoHandle.message = operation.message;
                        control.expireOperations.push(autoHandle);
                    }
                }
            }
            extendElements.push(control);
        }
    }

    updateProcessMessageNotices(notices: any[]) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof MessageNotice) {
                    extendElements.splice(i - 1, 1);
                }
            }
        }
        if (notices && notices.length > 0) {
            for (let n of notices) {
                const messageNotice = new MessageNotice(process.Model);
                messageNotice.sendType = n.sendType;
                messageNotice.sendTime = n.sendTime;
                messageNotice.subject = n.subject;
                messageNotice.content = n.content;
                // messageNotice.receiver = n.receiver;
                // messageNotice.receiverText = n.receiverText;
                n.receivers = n.receivers.filter(r => r.type);
                if (n.receivers && n.receivers.length > 0) {
                    const receivers = n.receivers.filter(r => r.type === 'userParticipant'
                        || (r.type === 'processParticipant' && r.processParticipant !== 'activityExecutorParticipant'));
                    const participants = n.receivers.filter(r => r.type === 'variableParticipant' || r.type === 'positionParticipant'
                        || (r.type === 'processParticipant' && r.processParticipant === 'activityExecutorParticipant'));
                    if (receivers && receivers.length > 0) {
                        let receiver = '';
                        let receiverName = '';
                        for (const r of receivers) {
                            if (r.type === 'processParticipant') {
                                receiver += (r.processParticipant + ',');
                                if (r.processParticipant === 'procInstCreator') {
                                    receiverName += '流程发起人,'
                                } else if (r.processParticipant === 'workItemParticipant') {
                                    receiverName += '任务接收人,'
                                }
                            } else if (r.type === 'userParticipant') {
                                receiver += (r.userIds + ',');
                                receiverName += (r.userNames + ',');
                            }
                        }
                        if (receiver) {
                            receiver = receiver.substring(0, receiver.length - 1);
                        }
                        if (receiverName) {
                            receiverName = receiverName.substring(0, receiverName.length - 1);
                        }
                        messageNotice.receiver = receiver;
                        messageNotice.receiverText = receiverName;
                    }
                    if (participants && participants.length > 0) {
                        const receiverExpressions = new Array<ParticipantExpression>();
                        for (const p of participants) {
                            if (p.type === 'processParticipant') {
                                let activityExecutor = new ActivityExecutorParticipantExpression(process.Model);
                                activityExecutor.id = BpmnModelHelper.GenerateElementId();
                                activityExecutor.activityDefId = p.activityDefId;
                                activityExecutor.activityDefName = p.activityDefName;
                                receiverExpressions.push(activityExecutor);
                            }
                            if (p.type === 'variableParticipant') {
                                let variableParticipant = new UserParticipantExpression(process.Model, p.variableParticipant, p.variableName, p.orgType);
                                variableParticipant.id = BpmnModelHelper.GenerateElementId();
                                variableParticipant.Tag = 'expression';
                                variableParticipant.OrgType = p.orgType;
                                receiverExpressions.push(variableParticipant);
                            }
                            if (p.type === 'positionParticipant') {
                                let posiParticipant = new PositionParticipantExpression(process.Model, p.positionId, p.positionName);
                                posiParticipant.id = BpmnModelHelper.GenerateElementId();
                                posiParticipant.PositionType = p.positionType;
                                if (p.positionType === '1') {
                                    posiParticipant.OrgId = p.orgId;
                                    posiParticipant.OrgType = p.orgType;
                                }
                                receiverExpressions.push(posiParticipant);
                            }
                        }
                        messageNotice.receiverExpressions = receiverExpressions;
                    }
                }

                if (n.smsMessageTemplate) {
                    const smsMessageTemplate = new SmsMessageTemplate(process.Model);
                    smsMessageTemplate.code = n.smsMessageTemplate.code;
                    smsMessageTemplate.content = n.smsMessageTemplate.content;
                    smsMessageTemplate.smsMessageVariables = new Array();
                    if (n.smsMessageTemplate.smsMessageVariables && n.smsMessageTemplate.smsMessageVariables.length > 0) {
                        for (const v of n.smsMessageTemplate.smsMessageVariables) {
                            const variable = new SmsMessageVariable(process.Model);
                            variable.code = v.code;
                            variable.value = v.value;
                            variable.tag = v.tag;
                            smsMessageTemplate.smsMessageVariables.push(variable);
                        }
                    }
                    messageNotice.smsMessageTemplate = smsMessageTemplate;
                }

                extendElements.push(messageNotice);
            }
        }
    }

    updateImageSettings(imageSettings: any) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        if (extendElements.length > 0) {
            for (let i = extendElements.length; i > 0; i--) {
                if (extendElements[i - 1] instanceof ImageSettings) {
                    extendElements.splice(i - 1, 1);
                }
            }
        }
        if (imageSettings) {
            const imageSetting = new ImageSettings(process.Model);
            imageSetting.imageOperation = imageSettings.imageOperation;
            extendElements.push(imageSetting);
        }
    }

    /**
     * 更新扩展属性
     * @param bpmnModel
     * @param value
     */
    resetExtendProperty(value: any[]) {
        const process = this.process;
        let extendElements = process.GetExtensionElements();
        for (const o of value) {
            for (const e of extendElements) {
                if (e instanceof ExtendProperty && e.key === o.code) {
                    const index = extendElements.indexOf(e);
                    if (index > -1) {
                        extendElements.splice(index, 1);
                    }
                }
            }
        }
        let attributes = value as any;
        if (attributes) {
            attributes.forEach(a => {
                let item = new ExtendProperty(process.Model);
                item.key = a.code;
                item.value = a.value;
                extendElements.push(item);
            });
        }
    }

    updateEnableWholeOrderRetrieve(wholeOrderRetrieve: boolean) {
        let extendElements = this.process.GetExtensionElements();
        if (extendElements !== null && extendElements.length > 0) {
            for (let i in extendElements) {
                if (extendElements[i] instanceof ProcessHeader) {
                    (extendElements[i] as ProcessHeader).enableWholeOrderRetrieve = wholeOrderRetrieve;
                    return;
                }
            }
        }
    }
}
